import React, {
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import api from 'api';
import classNames from 'classnames';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import InputField from 'components/InputField';
import { LEAD_STAGES, LeadStages } from 'models/Lead';
import { Loader } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import ListingContext from '../../../../providers/Listing/Listing.context';
import { CheckboxGroupPlaceholders } from './checkboxplaceholders';
import { SUCCESS_MESSAGES } from 'request-responses';
import { FileAccessSelector } from 'components/FileAccessSelector';
import {
  StepWorkflowConfiguration,
  WorkflowTemplateValues,
} from 'models/WorkflowConfiguration';
import useSpecificRules from '../../hooks/useSpecificRules';
import useGeneralRules from '../../hooks/useGeneralRules';
import { Link } from 'react-router-dom';
import DealContext from '../../../../providers/Deal';

import './WorkflowTemplate.styles.scss';
import './WorkflowTemplate.styles.responsive.scss';

type WorkflowTemplateProps = {
  className?: string;
  page: LeadStages;
};

const WorkflowTemplate: React.FC<WorkflowTemplateProps> = ({
  className,
  page,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAccessLoading, setIsAccessLoading] = useState(false);

  const [data, setData] = useState<StepWorkflowConfiguration>();

  const { workflowConfigurationStep, dataRoomNodes, checkedFilesIds } =
    data || {};

  const {
    immediatelyAcceptVerifiedBuyers,
    automaticallySendNDA,
    automaticallySendNDAEnabled,
    maxCapacity,
    maxCapacityEnabled,
  } = workflowConfigurationStep || {};

  const initialValues: WorkflowTemplateValues = useMemo(
    () => ({
      maxCapacity,
      generalRules: { immediatelyAcceptVerifiedBuyers },
      specificRules: { automaticallySendNDA },
    }),
    [automaticallySendNDA, immediatelyAcceptVerifiedBuyers, maxCapacity],
  );

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    canAccessDataRoom,
    workflowConfiguration: { _id: workflowId },
  } = useContext(ListingContext);
  const { leadStagesCopies } = useContext(DealContext);

  const classes = classNames(className, 'workflow-template');

  const { t } = useTranslation();

  const generalRulesOptions = useGeneralRules();
  const specificRulesOptions = useSpecificRules({
    automaticallySendNDA,
    automaticallySendNDAEnabled,
  });

  const hasSpecificRules = useMemo(
    () => !!specificRulesOptions.length,
    [specificRulesOptions.length],
  );

  const showSpecificRules = useMemo(
    () => isLoading || hasSpecificRules || page !== LEAD_STAGES.ENQUIRE,
    [hasSpecificRules, isLoading, page],
  );

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.portfolio.seller.getWorkflowConfiguration(
          workflowId,
          page,
        );

        setData(data);
      } catch (err) {
        popServerError(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [page, workflowId, popServerError]);

  const onSubmit = useCallback(
    async (values, { getState }) => {
      try {
        const { modified } = getState();

        const updateValues = Object.entries(modified).reduce(
          (acc, [key, value]) => {
            if (value) {
              const [prefix, ...rest] = key.split('.');
              const sufix = rest.join('.');

              if (sufix) {
                acc[sufix] = values[prefix][sufix];
              } else {
                acc[key] = values[key];
              }
            }

            return acc;
          },
          {} as any,
        );

        if (Object.values(updateValues).length) {
          const { data } = await api.portfolio.seller.saveWorkflowConfiguration(
            workflowId,
            page,
            updateValues,
          );

          popSuccess(t(SUCCESS_MESSAGES.UPDATED_WORKFLOW_CONFIGURATION));
          setData((prev) => ({ ...prev, ...data }));
        }
      } catch (e) {
        popServerError(e);
      }
    },
    [page, popServerError, popSuccess, t, workflowId],
  );

  const updateFileAccess = useCallback(
    async ({
      dataRoomNodeId,
      enable,
    }: {
      dataRoomNodeId: string;
      enable: boolean;
    }) => {
      try {
        setIsAccessLoading(true);
        const {
          data: { checkedFilesIds },
        } = await api.portfolio.seller.saveWorkflowConfiguration(
          workflowId,
          page,
          {
            dataRoomDocumentsAvailability: [
              { dataRoomNodeId, checked: enable },
            ],
          },
        );

        popSuccess(t(SUCCESS_MESSAGES.UPDATED_WORKFLOW_CONFIGURATION));

        setData((prev) => ({ ...prev, checkedFilesIds }));
      } catch (e) {
        popServerError(e);
      } finally {
        setIsAccessLoading(false);
      }
    },
    [page, popServerError, popSuccess, t, workflowId],
  );

  const getChildrenNodes = useCallback(
    async (id?: string) => {
      const {
        data: { dataRoomNodes, checkedFilesIds },
      } = await api.portfolio.seller.getWorkflowConfiguration(
        workflowId,
        page,
        id,
      );

      setData((prev) => ({ ...prev, checkedFilesIds }));

      return dataRoomNodes;
    },
    [page, workflowId],
  );

  const getBaseNodes = useCallback(() => dataRoomNodes, [dataRoomNodes]);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, submitting }) => {
        return (
          <div className={classes}>
            {(submitting || isAccessLoading) && <Loader />}

            <form onSubmit={handleSubmit} onChange={handleSubmit}>
              <div className="workflow-template__form-content">
                <section className="workflow-template__rules-section">
                  <p className="wft-title">{t('generalRules')}</p>
                  {isLoading ? (
                    <CheckboxGroupPlaceholders
                      itemCount={1}
                      className="workflow-template-placeholder"
                    />
                  ) : (
                    <CheckboxFieldGroup
                      name="generalRules"
                      options={generalRulesOptions}
                    />
                  )}

                  {showSpecificRules && (
                    <>
                      <p className="wft-title">{t('specificRules')}</p>
                      <p>{t('setRulesThatApply')}</p>
                    </>
                  )}
                  {isLoading ? (
                    <CheckboxGroupPlaceholders
                      itemCount={1}
                      className="workflow-template-placeholder"
                    />
                  ) : hasSpecificRules ? (
                    <CheckboxFieldGroup
                      name="specificRules"
                      options={specificRulesOptions}
                    />
                  ) : null}

                  {page !== LEAD_STAGES.ENQUIRE ? (
                    <Field
                      name="maxCapacity"
                      component={InputField}
                      label={t('setMaxCapacity')}
                      disabled={!maxCapacityEnabled}
                    />
                  ) : (
                    <div className="qualificiations-section">
                      <p>{t('qualification')}</p>
                      <Link to="../../qualification-questions">
                        {t('seeQualificationQuestions')}
                      </Link>
                    </div>
                  )}
                </section>

                {canAccessDataRoom && (
                  <section className="workflow-template__documents-section">
                    <p className="wft-title">{t('files')}</p>
                    <p>
                      {`${t('thisDocumentsWillBeAccessible')} "${t(
                        leadStagesCopies[page],
                      )}" ${t('stage')}`}
                    </p>

                    {isLoading ? (
                      <CheckboxGroupPlaceholders
                        itemCount={4}
                        className="workflow-template-placeholder"
                      />
                    ) : (
                      <FileAccessSelector
                        className="workflow-template__documents-section__documents"
                        checkedFilesIds={checkedFilesIds}
                        getBaseNodes={getBaseNodes}
                        updateFileAccess={updateFileAccess}
                        getChildrenForNode={getChildrenNodes}
                      />
                    )}
                  </section>
                )}
              </div>
            </form>
          </div>
        );
      }}
    />
  );
};
export default WorkflowTemplate;
